/**
 * LinkedIn Scraper Service
 * Handles scraping LinkedIn profiles using the pulpminer.com API
 */

const LINKEDIN_SCRAPER_API_URL = 'https://tp.pressupwards.com/scrape-linkedin';
const API_BEARER_TOKEN = 'Bearer your-secret-api-key-12345';

/**
 * Extract LinkedIn username from LinkedIn URL
 * @param {string} linkedinUrl - Full LinkedIn profile URL
 * @returns {string|null} - Extracted username or null if invalid
 */
export function extractLinkedInUsername(linkedinUrl) {
  if (!linkedinUrl || typeof linkedinUrl !== 'string') {
    return null;
  }

  // Clean the URL and extract username
  const cleanUrl = linkedinUrl.trim();
  
  // Match pattern: https://www.linkedin.com/in/username or linkedin.com/in/username
  const match = cleanUrl.match(/(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/([^\/\?#]+)/i);
  
  if (match && match[1]) {
    return match[1].toLowerCase();
  }
  
  return null;
}

/**
 * Scrape LinkedIn profile data using new API
 * @param {string} linkedinUrl - Full LinkedIn profile URL
 * @returns {Promise<Object>} - Scraped profile data
 */
export async function scrapeLinkedInProfile(linkedinUrl) {
  if (!linkedinUrl) {
    throw new Error('LinkedIn URL is required');
  }

  try {
    const response = await fetch(LINKEDIN_SCRAPER_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': API_BEARER_TOKEN
      },
      body: JSON.stringify({
        url: linkedinUrl
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`LinkedIn scraping failed: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    
    // Return structured data with new format
    return {
      success: true,
      linkedinUrl: linkedinUrl,
      data: {
        name: data.Name || '',
        about: data.About || '',
        highlights: data.Highlights || [],
        experience: data.Experience || [],
        education: data.Education || [],
        rawData: data
      },
      scrapedAt: new Date().toISOString(),
      source: 'talkingpoints'
    };

  } catch (error) {
    console.error('LinkedIn scraping error:', error);
    return {
      success: false,
      linkedinUrl: linkedinUrl,
      error: error.message,
      scrapedAt: new Date().toISOString(),
      source: 'talkingpoints'
    };
  }
}

/**
 * Process multiple LinkedIn URLs and scrape their profiles
 * @param {Array<string>} linkedinUrls - Array of LinkedIn URLs
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<Array<Object>>} - Array of scraping results
 */
export async function scrapeMultipleLinkedInProfiles(linkedinUrls, onProgress = null) {
  const results = [];
  const total = linkedinUrls.length;

  for (let i = 0; i < linkedinUrls.length; i++) {
    const url = linkedinUrls[i];
    
    if (onProgress) {
      onProgress({
        current: i + 1,
        total: total,
        url: url
      });
    }

    if (!isValidLinkedInUrl(url)) {
      results.push({
        success: false,
        linkedinUrl: url,
        error: 'Invalid LinkedIn URL format',
        scrapedAt: new Date().toISOString()
      });
      continue;
    }

    try {
      const result = await scrapeLinkedInProfile(url);
      results.push(result);

      // Add delay between requests to avoid rate limiting
      if (i < linkedinUrls.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    } catch (error) {
      results.push({
        success: false,
        linkedinUrl: url,
        error: error.message,
        scrapedAt: new Date().toISOString()
      });
    }
  }

  return results;
}

/**
 * Validate LinkedIn URL format
 * @param {string} url - LinkedIn URL to validate
 * @returns {boolean} - True if valid LinkedIn URL
 */
export function isValidLinkedInUrl(url) {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const linkedinPattern = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9\-]+\/?(\?.*)?$/i;
  return linkedinPattern.test(url.trim());
}

/**
 * Extract useful information from LinkedIn scraping result for opener generation
 * @param {Object} linkedinData - Raw LinkedIn data from scraping
 * @returns {Object} - Processed data for opener generation
 */
export function processLinkedInDataForOpeners(linkedinData) {
  if (!linkedinData || !linkedinData.success || !linkedinData.data) {
    return null;
  }

  // The new API returns data directly in the data field
  const data = linkedinData.data;
  
  // Process experience data
  let processedExperience = null;
  if (data.experience && Array.isArray(data.experience)) {
    processedExperience = data.experience.map(exp => {
      const position = exp.Position || '';
      const details = exp.Details || '';
      const years = exp.Years || '';
      return `${position}${details ? ': ' + details : ''}${years ? ' (' + years + ')' : ''}`;
    }).join('; ');
  }
  
  // Process education data
  let processedEducation = null;
  if (data.education && Array.isArray(data.education)) {
    processedEducation = data.education.map(edu => {
      const degree = edu.Degree || '';
      const institution = edu.Institution || '';
      return `${degree}${institution ? ' from ' + institution : ''}`;
    }).join('; ');
  }
  
  // Process highlights/recent activity
  let processedHighlights = null;
  if (data.highlights && Array.isArray(data.highlights)) {
    processedHighlights = data.highlights.map(highlight => {
      return highlight.Title || '';
    }).filter(title => title).join('; ');
  }
  
  return {
    name: data.name || null,
    headline: data.about || null, // 'About' section serves as headline/summary
    company: null, // Not directly available in this API response format
    location: null, // Not available in this API response format
    industry: null, // Not available in this API response format
    experience: processedExperience,
    education: processedEducation,
    skills: null, // Not available in this API response format
    summary: data.about || null,
    connections: null, // Not available in this API response format
    profileUrl: null, // Not available in this API response format
    highlights: processedHighlights, // Recent posts/articles
    rawData: data.rawData // Keep raw data for debugging
  };
}